import { createContext, FC, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuthContext } from '../AuthProvider';
import posthog from 'posthog-js';
import { LogError } from 'src/lib';

interface FeatureFlagContextType {
    isFeatureEnabled: (featureName: string) => boolean;
}

export const FeatureFlagContext = createContext<FeatureFlagContextType>({
    isFeatureEnabled: () => false,
});

export const FeatureFlagProvider: FC = ({ children }) => {
    const { user } = useAuthContext();
    const featureFlagsRef = useRef<{ [key: string]: boolean }>({});

    useEffect(() => {
        if (user) {
            posthog.identify(user.id.toString());
        }
    }, [user]);

    const isFeatureEnabled = useCallback((featureName: string): boolean => {
        if (featureFlagsRef.current[featureName] !== undefined) {
            return featureFlagsRef.current[featureName];
        }

        try {
            const isEnabled = posthog.isFeatureEnabled(featureName);
            featureFlagsRef.current[featureName] = isEnabled;
            return isEnabled;
        } catch (error) {
            LogError(error);
            return false;
        }
    }, []);

    const value = useMemo(() => ({ isFeatureEnabled }), [isFeatureEnabled]);

    return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};
